import React, { FC, useEffect, useRef } from 'react';
import {
  ParticularTravelsFiltersFormType,
  ParticularTravelStatus,
  StatusPayment
} from '../../models/particularTravelsModel';
import { Button } from '@vadiun/react-components';
import { Field, Form, Formik, FormikProps } from 'formik';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import { TextField, CheckboxWithLabel } from 'formik-material-ui';
import * as Yup from 'yup';
import ParticularUserSelector from 'shared/components/form/ParticularUserSelector';
import StatusPaymentSelector from 'shared/components/form/StatusPaymentSelector';
import RequiredBillSelector from 'shared/components/form/RequiredBillSelector';
import { FilterCategoryTitle } from 'app/viaje/viajeTraslado/viajeTraslado/components/FilterCategoryTitle';

const ParticularTravelsFiltersSchema = Yup.object({
  id: Yup.string().nullable(),
  state: Yup.string().nullable(),
  status_payment: Yup.string().nullable(),
  dateFrom: Yup.mixed(),
  dateTo: Yup.mixed(),
  clientId: Yup.number().nullable(),
  has_required_bill: Yup.string().nullable()
});

interface Props {
  onSubmit: (values: ParticularTravelsFiltersFormType) => Promise<void>;
  initialValues: ParticularTravelsFiltersFormType;
  setSelectedClientLabel: (label: string) => void;
}

const ParticularTravelsFilters: FC<Props> = ({
  onSubmit,
  initialValues,
  setSelectedClientLabel
}) => {
  const formRef = useRef<FormikProps<ParticularTravelsFiltersFormType>>(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.setValues(initialValues);
    }
  }, [initialValues]);

  return (
    <>
      <Formik<ParticularTravelsFiltersFormType>
        innerRef={formRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ParticularTravelsFiltersSchema}
        onSubmit={onSubmit}
      >
        {({ submitForm, values, setFieldValue }) => (
          <Form className="max-w-lg gap-2 p-8">
            <div>
              <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
                <FilterCategoryTitle title="Periodo" />
                <div className="grid grid-cols-2 gap-x-3">
                  <Field
                    component={KeyboardDateTimePicker}
                    label="Desde"
                    name="dateFrom"
                    ampm={false}
                    inputVariant="outlined"
                    margin="dense"
                    format="DD/MM/YYYY HH:mm"
                  />
                  <Field
                    component={KeyboardDateTimePicker}
                    label="Hasta"
                    name="dateTo"
                    ampm={false}
                    inputVariant="outlined"
                    margin="dense"
                    format="DD/MM/YYYY HH:mm"
                  />
                </div>
              </div>

              <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
                <FilterCategoryTitle title="Reserva" />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    name="id"
                    label="ID Reserva"
                    variant="outlined"
                    component={TextField}
                    margin="dense"
                    type="number"
                    fullWidth
                  />
                  <ParticularUserSelector
                    name="clientId"
                    placeholder="Cliente"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    onLabelChange={setSelectedClientLabel}
                  />
                  <div className="col-span-2">
                    <StatusPaymentSelector
                      name="status_payment"
                      placeholder="Estado de Pago"
                    />
                  </div>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name="state"
                    color="primary"
                    Label={{ label: 'Facturado' }}
                    disabled={
                      values.status_payment !== StatusPayment.PAID &&
                      values.status_payment !== StatusPayment.MANUAL_PAYMENT && 
                      values.status_payment !== null
                    }
                    onChange={(e) => {
                      setFieldValue(
                        'state',
                        e.target.checked
                          ? ParticularTravelStatus.FACTURADO
                          : false
                      );
                      if (
                        e.target.checked &&
                        !(
                          values.status_payment === StatusPayment.PAID ||
                          values.status_payment === StatusPayment.MANUAL_PAYMENT
                        )
                      ) {
                        setFieldValue('status_payment', null);
                      }
                    }}
                  />
                  <div className="col-span-2">
                    <RequiredBillSelector
                      name="has_required_bill"
                      placeholder="Factura requerida"
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-1 flex justify-end pt-4">
                <Button onClick={submitForm} variant="contained">
                  Buscar
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ParticularTravelsFilters;
